import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram } from "lucide-react";
import { api } from "../lib/api";

export default function LatestDrop() {
  const [drops, setDrops] = useState([]);

  useEffect(() => {
    api.get("/drops").then((r) => setDrops(r.data || [])).catch(() => {});
  }, []);

  if (drops.length === 0) return null;

  const featured = drops.find((d) => d.is_featured) || drops[0];
  const rest = drops.filter((d) => d.id !== featured.id).slice(0, 4);

  return (
    <section data-testid="latest-drop" className="section-y bg-ink relative overflow-hidden">
      <div className="container-x">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="overline">Latest drop</p>
            <h2 className="mt-3 font-display text-5xl md:text-7xl text-white">
              Behind <span className="text-rose">the bling.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 text-sm uppercase tracking-wider">
            One-of-one pieces from the studio. Each piece pressed, beaded and bedazzled by hand.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          {/* Featured drop */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid={`drop-featured-${featured.id}`}
            className="lg:col-span-7 relative group"
          >
            <div className="splat-pink absolute -top-10 -left-10 z-0" />
            <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden border-2 border-white">
              <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute -top-3 left-6 tag-rose !rotate-0">{featured.tag || "FEATURED"}</div>

              <div className="absolute bottom-0 inset-x-0 p-6 md:p-10">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">{featured.subtitle}</p>
                <h3 className="font-display text-4xl md:text-6xl text-white leading-none">{featured.title}</h3>
                {featured.description && (
                  <p className="mt-4 text-white/85 text-sm md:text-base max-w-lg leading-relaxed">{featured.description}</p>
                )}
                {featured.instagram_url && (
                  <a href={featured.instagram_url} target="_blank" rel="noreferrer"
                    className="mt-5 inline-flex items-center gap-2 bg-rose text-white border-2 border-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] hover:bg-gold hover:text-ink transition"
                    style={{ boxShadow: "4px 4px 0 #FFFFFF" }}
                    data-testid="drop-featured-link"
                  >
                    <Instagram size={14} /> See on IG <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          {/* Recent drops grid */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            {rest.length === 0 ? (
              <div className="col-span-2 card-brutal flex items-center justify-center text-white/60 text-center text-sm uppercase tracking-wider">
                More drops dropping soon.
              </div>
            ) : (
              rest.map((d, i) => (
                <motion.a
                  key={d.id}
                  href={d.instagram_url || "#"}
                  target={d.instagram_url ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`drop-thumb-${d.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="relative group aspect-[4/5] overflow-hidden border-2 border-white"
                >
                  <img src={d.image_url} alt={d.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-ink/50 group-hover:bg-ink/30 transition-colors" />
                  <div className="absolute top-2 left-2 tag-sticker !text-[9px]">{d.tag || "DROP"}</div>
                  <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-ink to-transparent">
                    <p className="text-[10px] uppercase tracking-wider text-gold font-bold">{d.subtitle || "—"}</p>
                    <p className="font-display text-xl text-white leading-tight mt-0.5">{d.title}</p>
                  </div>
                </motion.a>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
